import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/fetcher';
import {
  type Webhook,
  type WebhookEvent,
  fetchWebhookEvents,
  updateWebhook,
  webhookEventsKey,
  webhookListKey,
} from '@/lib/queries';

interface Props {
  webhook: Webhook | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WebhookEditDialog({ webhook, open, onOpenChange }: Props) {
  const [url, setUrl] = React.useState(webhook?.url ?? '');
  const [description, setDescription] = React.useState(
    webhook?.description ?? '',
  );
  const [events, setEvents] = React.useState<WebhookEvent[]>(
    webhook?.events ?? [],
  );
  const [active, setActive] = React.useState<boolean>(
    webhook?.active ?? true,
  );

  React.useEffect(() => {
    if (webhook) {
      setUrl(webhook.url);
      setDescription(webhook.description ?? '');
      setEvents(webhook.events);
      setActive(webhook.active);
    }
  }, [webhook]);

  // Brief 131 — live event registry (same hook shape as the create dialog).
  const eventsQuery = useQuery({
    queryKey: webhookEventsKey,
    queryFn: fetchWebhookEvents,
    staleTime: 5 * 60 * 1000,
  });
  const availableEvents = eventsQuery.data ?? [];

  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (input: Parameters<typeof updateWebhook>[1]) => {
      if (!webhook) throw new Error('no webhook selected');
      return updateWebhook(webhook.id, input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: webhookListKey });
      onOpenChange(false);
    },
  });

  if (!webhook) return null;

  const onToggleEvent = (event: WebhookEvent) => {
    setEvents((prev) =>
      prev.includes(event)
        ? prev.filter((e) => e !== event)
        : [...prev, event],
    );
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (events.length === 0) return;
    const input: Parameters<typeof updateWebhook>[1] = {
      url,
      events,
      description,
      active,
    };
    update.mutate(input);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Edit webhook</DialogTitle>
            <DialogDescription>
              Signing secret is unchanged. To rotate it, delete this
              webhook and create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-url">URL</Label>
            <Input
              id="edit-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Events</Label>
            {eventsQuery.isLoading ? (
              <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                Loading available event types…
              </div>
            ) : availableEvents.length === 0 ? (
              <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                No event types are registered yet. Webhooks will activate
                once your products fire their first event.
              </div>
            ) : (
              <div className="grid gap-2 rounded-md border p-3 text-sm">
                {availableEvents.map((event) => (
                  <label
                    key={event}
                    className="flex items-center gap-2 font-mono text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={events.includes(event)}
                      onChange={() => onToggleEvent(event)}
                      className="h-4 w-4 rounded border-input"
                    />
                    {event}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Input
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Active (deliveries fire when toggled on)
          </label>
          {update.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Update failed (
                {update.error instanceof ApiError
                  ? update.error.code
                  : 'unknown'}
                )
              </AlertTitle>
              <AlertDescription>
                {update.error instanceof Error
                  ? update.error.message
                  : 'Try again.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={update.isPending || events.length === 0}
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
